import React, { useMemo } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import { Link } from 'react-router-dom';
import Product from '../components/Product';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Paginate from '../components/Paginate';
import ProductCarousel from '../components/ProductCarousel';
import Meta from '../components/Meta';
import { useDispatch, useSelector } from 'react-redux';
import { useGetApprovedFilledShiftsQuery } from '../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice';
import { useGetPreShiftsQuery } from '../slices/preShiftApiSlice';

// ─── Simple hook to detect mobile screen width ────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
};

// Same localStorage keys the ward "Approve and update worked shifts" screens
// use for their "Past shifts only" toggle (admin: shiftWardList_showPastOnly,
// manager: mgr_shiftWardList_showPastOnly) — set to true before navigating so
// the card takes the user straight to the filtered, past-due view it counted.
const LS_PAST_ADMIN   = 'shiftWardList_showPastOnly';
const LS_PAST_MANAGER = 'mgr_shiftWardList_showPastOnly';

// ─── Shared manager ward-scope helper — same directorate/inclusion/own-ward
// rules used across the ward screens. Kept local to this file (rather than
// pulled from userInfo directly in each hook) so both cards below scope
// their counts identically. ─────────────────────────────────────────────
const useManagerWardScope = ({ skip }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const isAdmin = userInfo?.isAdmin === true;
  const isManager = userInfo?.isManager === true;
  const isReporter = userInfo?.isReporter === true;
  const managerDirectorate = userInfo?.directorateName || null;
  const managerOnlyOwnWard = userInfo?.onlyOwnWard === true;
  const managerWardInclusions = userInfo?.wardAreaInclusions || [];

  const visible = isAdmin || isManager || isReporter;

  // Only managers need the hospital-locations lookup to work out which wards
  // fall inside their directorate/own-ward/inclusions — admin and reporter
  // both see every ward unscoped (scopeToWard passes non-managers straight
  // through below), so this query is skipped for anyone but a manager.
  const { data: hospitalLocations = [], isLoading: loadingLocations } = useGetHospitalLocationsQuery(undefined, {
    skip: skip || !visible || !isManager,
  });

  const inclusionIds = useMemo(
    () =>
      new Set(
        managerWardInclusions.map((w) => (typeof w === 'object' && w !== null ? w._id : w))
      ),
    [managerWardInclusions]
  );

  const wardToDirectorateMap = useMemo(() => {
    if (!hospitalLocations.length) return {};
    const map = {};
    hospitalLocations.forEach((loc) => {
      if (loc.wardDepartmentArea && loc.directorate) {
        map[loc.wardDepartmentArea] = loc.directorate;
      }
    });
    return map;
  }, [hospitalLocations]);

  const includedWardNames = useMemo(() => {
    if (!hospitalLocations.length || inclusionIds.size === 0) return [];
    return hospitalLocations
      .filter((loc) => inclusionIds.has(loc._id))
      .map((loc) => loc.wardDepartmentArea)
      .filter(Boolean);
  }, [hospitalLocations, inclusionIds]);

  // Filters an array of records down to this manager's ward scope, given a
  // function that pulls the ward name off each record. Admins get the array
  // back untouched.
  const scopeToWard = (records, getWard) => {
    if (isAdmin || !isManager) return records;

    if (managerOnlyOwnWard) {
      return records.filter((r) => {
        const ward = getWard(r);
        if (!ward) return false;
        return ward === userInfo?.speciality || includedWardNames.includes(ward);
      });
    }

    if (managerDirectorate) {
      return records.filter((r) => {
        const ward = getWard(r);
        if (!ward) return false;
        return wardToDirectorateMap[ward] === managerDirectorate || includedWardNames.includes(ward);
      });
    }

    if (includedWardNames.length > 0) {
      return records.filter((r) => includedWardNames.includes(getWard(r)));
    }

    return records;
  };

  return {
    visible,
    isAdmin,
    isManager,
    isReporter,
    managerDirectorate,
    managerOnlyOwnWard,
    //loadingLocations: visible && !isAdmin && loadingLocations,
    loadingLocations: visible && isManager && loadingLocations,
    scopeToWard,
  };
};

// ─── Outstanding shift-approvals count, scoped the same way the ward
// "Approve and update worked shifts" screens scope their rows: admin sees
// everything, manager sees their own ward (if onlyOwnWard) or directorate,
// plus any admin-configured ward inclusions. "Waiting for approval" = shift
// already ended (endDateTime in the past) but still sitting in the
// approvals list — the same set the "Past shifts only" toggle on that
// screen filters down to. ─────────────────────────────────────────────────
const useOutstandingApprovalsCount = () => {
  const scope = useManagerWardScope({ skip: false });
  const { visible, isAdmin, isManager, managerDirectorate, managerOnlyOwnWard, loadingLocations, scopeToWard } = scope;

  const { data: shifts, isLoading: loadingShifts } = useGetApprovedFilledShiftsQuery(undefined, {
    skip: !visible,
  });

  const count = useMemo(() => {
    if (!shifts) return 0;
    const scoped = scopeToWard(shifts, (s) => s.wardDepartmentArea);
    const now = new Date();
    return scoped.filter((s) => new Date(s.endDateTime) < now).length;
  }, [shifts, scopeToWard]);

  return {
    visible,
    isAdmin,
    isManager,
    managerDirectorate,
    managerOnlyOwnWard,
    count,
    isLoading: visible && (loadingShifts || loadingLocations),
  };
};

const ApprovalsCard = () => {
  const navigate = useNavigate();
  const { visible, isAdmin, isManager, isReporter, managerDirectorate, managerOnlyOwnWard, count, isLoading } = useOutstandingApprovalsCount();

  if (!visible) return null;

  const clickable = isAdmin || isManager; // reporter has no write screen to land on — view-only

  const handleClick = () => {
    if (!clickable) return;
    try {
      localStorage.setItem(isAdmin ? LS_PAST_ADMIN : LS_PAST_MANAGER, JSON.stringify(true));
    } catch {
      // localStorage can throw in private browsing / storage-restricted contexts — the
      // screen just falls back to its default "All shifts" view in that case.
    }
    navigate(isAdmin ? '/admin/approved-filled' : '/manager/approved-filled');
  };

  const scopeLabel = managerOnlyOwnWard
    ? ' for your ward'
    : managerDirectorate
      ? ` for ${managerDirectorate}`
      : '';

  return (
    <Card
      className="mb-4 shadow-sm"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) handleClick();
      }}
      style={{ cursor: clickable ? 'pointer' : 'default', borderLeft: '6px solid #fd7e14' }}
    >
      <Card.Body className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <Card.Title className="mb-1">Shifts Awaiting Approval</Card.Title>
          <Card.Text className="text-muted mb-0">
            Past shifts still needing their worked hours approved{isManager && !isAdmin ? scopeLabel : ''}
            {isReporter ? ' (view only)' : '. Click to review.'}
          </Card.Text>
        </div>
        {isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Badge
            bg={count > 0 ? 'danger' : 'secondary'}
            style={{ fontSize: '1.4rem', padding: '0.5em 0.85em' }}
          >
            {count}
          </Badge>
        )}
      </Card.Body>
    </Card>
  );
};

// ─── Outstanding pre-shift-request approvals count. A pre-shift request is
// "awaiting approval" while preShiftStatus === 'Pending' (see
// preShiftController.js — approvePreShift/rejectPreShift move it to
// 'Approved'/'Rejected'). useGetPreShiftsQuery already comes back
// ward-scoped from the backend for managers (getPreShifts branches on
// req.user.isAdmin server-side), so scopeToWard here is defence-in-depth,
// same as the shift-approvals count above. ─────────────────────────────────
const useOutstandingPreShiftApprovalsCount = () => {
  const scope = useManagerWardScope({ skip: false });
  const { visible, isAdmin, isManager, managerDirectorate, managerOnlyOwnWard, loadingLocations, scopeToWard } = scope;

  const { data: preShifts, isLoading: loadingPreShifts } = useGetPreShiftsQuery(undefined, {
    skip: !visible,
  });

  const count = useMemo(() => {
    if (!preShifts) return 0;
    const scoped = scopeToWard(preShifts, (p) => p.wardDepartmentArea);
    return scoped.filter((p) => p.preShiftStatus === 'Pending').length;
  }, [preShifts, scopeToWard]);

  return {
    visible,
    isAdmin,
    isManager,
    managerDirectorate,
    managerOnlyOwnWard,
    count,
    isLoading: visible && (loadingPreShifts || loadingLocations),
  };
};

const PreShiftApprovalsCard = () => {
  const navigate = useNavigate();
  const { visible, isAdmin, isManager, isReporter, managerDirectorate, managerOnlyOwnWard, count, isLoading } = useOutstandingPreShiftApprovalsCount();

  if (!visible) return null;

  const clickable = isAdmin || isManager;

  const handleClick = () => {
    if (!clickable) return;
    navigate(isAdmin ? '/admin/ward-preshifts' : '/manager/ward-preshifts');
  };

  const scopeLabel = managerOnlyOwnWard
    ? ' for your ward'
    : managerDirectorate
      ? ` for ${managerDirectorate}`
      : '';

  return (
    <Card
      className="mb-4 shadow-sm"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) handleClick();
      }}
      style={{ cursor: clickable ? 'pointer' : 'default', borderLeft: '6px solid #6f42c1' }}
    >
      <Card.Body className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <Card.Title className="mb-1">Pre-Shift Requests Awaiting Approval</Card.Title>
          <Card.Text className="text-muted mb-0">
            New shift requests waiting for admin sign-off{isManager && !isAdmin ? scopeLabel : ''}
            {isReporter ? ' (view only)' : '. Click to review.'}
          </Card.Text>
        </div>
        {isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <Badge
            bg={count > 0 ? 'danger' : 'secondary'}
            style={{ fontSize: '1.4rem', padding: '0.5em 0.85em' }}
          >
            {count}
          </Badge>
        )}
      </Card.Body>
    </Card>
  );
};

// ─── Logged-in staff member's own shift summary. Reuses the exact same
// useGetApprovedFilledShiftsQuery as ApprovalsCard above (RTK Query dedupes
// the two calls into one request) — that query already returns only shifts
// that are filled (staff or agency), stage1Approval true, isActive, not
// agencyCancelled, and not yet finalised (actualStatus not Complete/
// Not-Worked). Splitting THOSE shifts by whether filledBy is this user and
// whether endDateTime has passed gives exactly the two buckets asked for:
//   - Waiting for Approval: assigned to me, already ended, admin/manager
//     hasn't recorded Worked/Not-Worked yet.
//   - Upcoming Shifts: assigned to me, end time hasn't passed yet.
// ──────────────────────────────────────────────────────────────────────────
const useMyShiftsSummary = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const myId = userInfo?._id ? String(userInfo._id) : null;

  const { data: shifts, isLoading } = useGetApprovedFilledShiftsQuery(undefined, {
    skip: !myId,
  });

  const { waitingForApproval, upcoming } = useMemo(() => {
    if (!shifts || !myId) return { waitingForApproval: 0, upcoming: 0 };

    const now = new Date();
    let waiting = 0;
    let upcomingCount = 0;

    shifts.forEach((s) => {
      const filledById = s.filledBy && typeof s.filledBy === 'object' ? s.filledBy._id : s.filledBy;
      if (!filledById || String(filledById) !== myId) return;

      if (new Date(s.endDateTime) < now) {
        waiting += 1;
      } else {
        upcomingCount += 1;
      }
    });

    return { waitingForApproval: waiting, upcoming: upcomingCount };
  }, [shifts, myId]);

  return {
    visible: !!myId,
    isLoading: !!myId && isLoading,
    waitingForApproval,
    upcoming,
  };
};

const MyShiftsCard = () => {
  const navigate = useNavigate();
  const { visible, isLoading, waitingForApproval, upcoming } = useMyShiftsSummary();

  if (!visible) return null;

  const goToRoster = () => navigate('/my-roster');
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') goToRoster();
  };

  const tileStyle = (accent, bg) => ({
    cursor: 'pointer',
    border: `1px solid ${accent}`,
    backgroundColor: bg,
  });

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Body>
        <Card.Title className="mb-3">My Shifts</Card.Title>
        <Row className="g-3">
          <Col xs={6}>
            <div
              role="button"
              tabIndex={0}
              onClick={goToRoster}
              onKeyDown={handleKeyDown}
              className="p-3 rounded d-flex flex-column align-items-center text-center"
              style={tileStyle('#ffc107', '#fff8e1')}
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{waitingForApproval}</div>
              )}
              <div className="text-muted small mt-1">Waiting for Approval</div>
            </div>
          </Col>
          <Col xs={6}>
            <div
              role="button"
              tabIndex={0}
              onClick={goToRoster}
              onKeyDown={handleKeyDown}
              className="p-3 rounded d-flex flex-column align-items-center text-center"
              style={tileStyle('#0d6efd', '#e7f1ff')}
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>{upcoming}</div>
              )}
              <div className="text-muted small mt-1">Upcoming Shifts</div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const HomeScreen = () => {
  const { pageNumber, keyword } = useParams();
  const { userInfo } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();

  const accessType = userInfo?.isAdmin
    ? 'Admin'
    : userInfo?.isManager
      ? 'Manager'
      : 'User';

  const { data, isLoading, error } = useGetProductsQuery({
    keyword,
    accessType,
  });

  return (
    <>
      {!keyword ? (
        // ── Only show carousel on tablet/desktop (768px and above) ──
        isLoading ? null : error ? null : !isMobile && (
          <ProductCarousel products={data?.products || []} />
        )
      ) : (
        <Link to='/' className='btn btn-light mb-4'>
          Go Back
        </Link>
      )}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>
          {error?.data?.message || error.error}
        </Message>
      ) : (
        <>

          <h1>Select a Menu Option</h1>
          <Meta />
          <ApprovalsCard />
          <PreShiftApprovalsCard />
          <MyShiftsCard />
          
          <Row className='mx-0'>
            {data?.products?.map((product) => (
              <Col
                key={product._id}
                sm={12}
                md={6}
                lg={4}
                xl={3}
                className='px-0'
              >
                <Product product={product} />
              </Col>
            ))}
          </Row>
          <Paginate
            pages={data?.pages}
            page={data?.page}
            keyword={keyword ? keyword : ''}
          />
        </>
      )}
    </>
  );
};

export default HomeScreen;